// Bild-Matrix einer Variantenfamilie: Zeilen sind Bilder, Spalten sind Varianten, und in der
// Zelle steht die Anzeige-Reihenfolge dieses Bildes fuer diese Variante.
//
// Entwurf: docs/superpowers/specs/2026-08-12-kaufland-bildgrid-varianten-design.md
// Vorbild ist der Otto-Lister (docs/Otto Bilder.png), auf Wunsch des Betreibers.
//
// WARUM: Am 2026-08-12 hingen alle neun freigegebenen Bilder am Variantenvater, beide Kinder
// hatten null. Bei Kaufland ist der Vater kein Produkt -- gelistet werden die Kinder, und die
// gingen ohne Bild raus (`MISSING_ATTRIBUTES: ["Bild"]`). Diese Matrix verteilt sie.
//
// BACKEND, unveraendert wiederverwendet:
//   * `vario-article-images` mit `articleIds` -> alle DMS-Bilder je Artikel.
//   * `kaufland-release-images` je geaenderter Spalte -> Diff-Upsert der Whitelist. Achtung:
//     Was nicht in `releases` steht, wird geloescht -- es geht immer die VOLLSTAENDIGE Liste
//     einer Spalte raus.
//   * `vario.ensure_dms_share` haengt am Bild, nicht am Artikel, und verwendet bestehende
//     Shares wieder. Dasselbe Bild an drei Kindern erzeugt EINEN Share, nicht drei (gemessen).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import {
  assignImage,
  removeImage,
  moveImage,
  assignToAll,
  changedColumns,
  orderedResources,
  type GridState,
} from '../../lib/image-grid.js';
import { entferneGesperrte, istFreigebbar, sperrHinweis, sperrgrund } from '../../lib/bild-freigabe.js';

export interface GridCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

/** Eine Spalte der Matrix. */
export interface GridArticle {
  articleId: string;
  label: string;
  isParent: boolean;
}

interface ImageRow {
  resourceId: string;
  alias: string;
  mimeType: string;
  /** Kauflands Bildvorgaben, bewertet von vario-article-images (Format, Groesse). */
  releasable: boolean;
  blockReason: string | null;
  /** Nur bei freigegebenen Bildern gesetzt (Vario-DMS-Share). */
  publicUrl: string | null;
  /**
   * Der Artikel, aus dessen DMS dieses Bild stammt. Wird fuer die Vorschau ohne Freigabe
   * gebraucht: `vario-dms-image-proxy` verlangt Artikel UND Resource.
   */
  ownerArticleId: string;
}

/**
 * Blob-URLs der ueber den Proxy geladenen Vorschauen, ueber Neuzeichnungen hinweg.
 *
 * Ohne diesen Zwischenspeicher laedt jedes Neuzeichnen (also jeder Klick auf eine Zelle) alle
 * Vorschauen erneut. Der Proxy macht je Bild einen Vario-DMS-Aufruf samt OAuth-Refresh -- ein
 * Grid mit 9 Bildern und 4 Spalten wuerde Vario bei jedem Klick mit Anfragen ueberziehen.
 */
const vorschauCache = new Map<string, string>();

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

async function callFn<T>(ctx: GridCtx, name: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const eb = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: eb ? `${eb.code ?? '?'}: ${eb.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

interface RohBild {
  resourceId: string;
  alias?: string;
  mimeType?: string;
  publicUrl?: string | null;
  released?: boolean;
  sortOrder?: number | null;
  /** Fehlt bei aelteren Antworten der Function; dann gilt das Bild als geeignet. */
  releasable?: boolean;
  blockReason?: string | null;
}

/** Die Familie, fuer die das Grid gilt — der Variantenvater. */
export interface GridListing {
  storefront: string;
  vario_article_id: string;
  vario_listing_id: string;
  kaufland_category_id: number | null;
}

/**
 * Loest die Familie selbst auf und rendert dann das Grid.
 *
 * Bewusst eigenstaendig statt von der Varianten-Tabelle gefuettert: Das Grid ist seit dem
 * 2026-08-16 ein eigener Reiter neben Uebersicht und Varianten, nicht mehr ein Modal aus der
 * Varianten-Tabelle heraus. Es darf deshalb nicht davon abhaengen, dass jemand vorher die
 * Varianten-Tabelle geoeffnet hat.
 */
export async function renderVariantImageGridForListing(
  host: HTMLElement,
  ctx: GridCtx,
  listing: GridListing,
): Promise<void> {
  host.innerHTML = `<div class="klv-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Varianten …</div>`;

  const vc = await callFn<{ rows: Array<{ articleId: string; isParent: boolean; combinationLabel: string }> }>(
    ctx,
    'kaufland-listing-preview',
    {
      action: 'variant-check',
      storefront: listing.storefront,
      articleId: listing.vario_article_id,
      listingId: listing.vario_listing_id,
    },
  );
  if (!vc.ok) {
    host.innerHTML = `<div class="klv-empty klv-empty--error">Varianten konnten nicht geladen werden: ${escapeHtml(vc.error)}</div>`;
    return;
  }
  const spalten: GridArticle[] = (vc.value.rows ?? []).map((r) => ({
    articleId: r.articleId,
    label: r.isParent ? 'Vater' : (r.combinationLabel || r.articleId),
    isParent: r.isParent,
  }));
  if (spalten.length === 0) {
    host.innerHTML = `<div class="klv-empty">Zu diesem Artikel wurden keine Varianten gefunden.</div>`;
    return;
  }
  await renderVariantImageGrid(host, ctx, spalten);
}

export async function renderVariantImageGrid(
  host: HTMLElement,
  ctx: GridCtx,
  articles: GridArticle[],
): Promise<void> {
  host.innerHTML = `<div class="klv-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Bilder der Variantenfamilie …</div>`;

  const resp = await callFn<{ byArticle: Record<string, RohBild[]> }>(ctx, 'vario-article-images', {
    articleIds: articles.map((a) => a.articleId),
  });
  if (!resp.ok) {
    host.innerHTML = `<div class="klv-empty klv-empty--error">Bilder konnten nicht geladen werden: ${escapeHtml(resp.error)}</div>`;
    return;
  }
  const byArticle = resp.value.byArticle ?? {};

  // Zeilen = Vereinigung ALLER Bilder der Familie. Ein Bild, das nur am Vater haengt, muss
  // auftauchen -- es ist der haeufigste Fall und der Grund fuer diesen Reiter.
  const zeilen = new Map<string, ImageRow>();
  for (const a of articles) {
    for (const b of byArticle[a.articleId] ?? []) {
      if (!b?.resourceId || zeilen.has(b.resourceId)) continue;
      zeilen.set(b.resourceId, {
        resourceId: b.resourceId,
        alias: b.alias ?? b.resourceId,
        mimeType: b.mimeType ?? '',
        releasable: istFreigebbar(b),
        blockReason: b.blockReason ?? null,
        publicUrl: b.publicUrl ?? null,
        ownerArticleId: a.articleId,
      });
    }
  }

  // Ausgangszustand: was heute je Spalte freigegeben ist, in sort_order-Reihenfolge.
  const start: GridState = {};
  for (const a of articles) {
    start[a.articleId] = (byArticle[a.articleId] ?? [])
      .filter((b) => b.released)
      .sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0))
      .map((b) => b.resourceId);
  }

  if (zeilen.size === 0) {
    host.innerHTML = `<div class="klv-empty">Für diese Familie sind in Vario keine Bilder hinterlegt.</div>`;
    return;
  }

  const kindSpalten = articles.filter((a) => !a.isParent).map((a) => a.articleId);
  // Kauflands Bildvorgaben: gesperrte Bilder (Format, Groesse) aus den KIND-Spalten nehmen.
  // Der Start bleibt, wie er in der Datenbank steht, damit changedColumns die Entfernung
  // sieht und das Speichern sie loescht. Die Vater-Spalte bleibt: Sie wird nie gespeichert,
  // eine Entfernung dort waere eine Aenderung, die nie verschwindet.
  const gesperrt = new Set([...zeilen.values()].filter((z) => !z.releasable).map((z) => z.resourceId));
  const bereinigt = entferneGesperrte(start, gesperrt, kindSpalten);
  let zustand: GridState = bereinigt.state;

  const zeichnen = (): void => {
    // Scroll-Stand des Matrix-Bereichs merken, BEVOR wir ihn neu bauen.
    //
    // Jeder Klick auf eine Zelle zeichnet die ganze Tabelle per innerHTML neu, damit die
    // Nummern und der Aenderungszaehler stimmen. Dabei entsteht auch .klg-scroll neu, und
    // ein frischer Container startet bei scrollTop = 0 -- die Liste sprang also bei JEDER
    // Auswahl nach oben. Bei zwoelf Bildern war die Zelle danach ausser Sicht, man musste
    // vor jedem Klick neu scrollen. Gemeldet vom Betreiber am 2026-08-16.
    const alterBereich = host.querySelector<HTMLElement>('.klg-scroll');
    const merkeOben = alterBereich?.scrollTop ?? 0;
    const merkeLinks = alterBereich?.scrollLeft ?? 0;

    const kopf = articles.map((a) => `
      <th class="klg-col${a.isParent ? ' klg-col--parent' : ''}"${a.isParent ? ' title="Der Variantenvater ist bei Kaufland kein Produkt, er zeigt nur, woher die Bilder stammen."' : ''}>
        ${escapeHtml(a.label)}${a.isParent ? ' <span class="klg-parent-note">(nicht gelistet)</span>' : ''}
      </th>`).join('');

    const koerper = [...zeilen.values()].map((img) => {
      const zellen = articles.map((a) => {
        if (a.isParent) {
          const nr = orderedResources(zustand, a.articleId).indexOf(img.resourceId);
          return `<td class="klg-cell klg-cell--parent">${nr >= 0 ? `<span class="klg-num klg-num--off">${nr + 1}</span>` : ''}</td>`;
        }
        if (!img.releasable) {
          // Gesperrt (Kaufland-Format oder -Groesse): keine Zelle zum Zuordnen.
          return `<td class="klg-cell klg-cell--gesperrt" title="${escapeHtml(sperrgrund(img))}"></td>`;
        }
        const nr = orderedResources(zustand, a.articleId).indexOf(img.resourceId);
        return nr >= 0
          ? `<td class="klg-cell"><button type="button" class="klg-num" data-act="remove" data-col="${escapeHtml(a.articleId)}" data-res="${escapeHtml(img.resourceId)}" title="Zuordnung entfernen">${nr + 1}</button>
               <span class="klg-move">
                 <button type="button" class="klg-arrow" data-act="up" data-col="${escapeHtml(a.articleId)}" data-res="${escapeHtml(img.resourceId)}" title="nach vorn">▲</button>
                 <button type="button" class="klg-arrow" data-act="down" data-col="${escapeHtml(a.articleId)}" data-res="${escapeHtml(img.resourceId)}" title="nach hinten">▼</button>
               </span></td>`
          : `<td class="klg-cell"><button type="button" class="klg-empty" data-act="add" data-col="${escapeHtml(a.articleId)}" data-res="${escapeHtml(img.resourceId)}" title="Bild dieser Variante zuordnen"></button></td>`;
      }).join('');

      // Freigegeben: direkte Share-URL. Nicht freigegeben: ueber den DMS-Proxy nachladen.
      // Eine Freigabe ist KEINE Voraussetzung dafuer, ein Bild zu sehen -- man muss es ja
      // ansehen koennen, um zu entscheiden, ob man es freigibt.
      const zwischengespeichert = vorschauCache.get(img.resourceId);
      const vorschau = img.publicUrl
        ? `<img src="${escapeHtml(img.publicUrl)}" alt="" class="klg-thumb">`
        : zwischengespeichert
          ? `<img src="${escapeHtml(zwischengespeichert)}" alt="" class="klg-thumb">`
          : `<img class="klg-thumb klg-thumb--lade img-proxy-lazy" data-rid="${escapeHtml(img.resourceId)}" data-aid="${escapeHtml(img.ownerArticleId)}" alt="">`;

      // Der Inhalt liegt in einem eigenen Element, damit die Zelle `table-cell` bleibt.
      // Ein `display:flex` auf dem `td` selbst wuerde das Klebenbleiben beim Scrollen brechen.
      return `
        <tr${img.releasable ? '' : ' class="klg-row--gesperrt"'}>
          <td class="klg-bild">
            <div class="klg-bild-inner">
              ${vorschau}
              ${img.releasable
                ? `<button type="button" class="v-button v-button--sm klg-all" data-res="${escapeHtml(img.resourceId)}">alle</button>`
                : `<button type="button" class="v-button v-button--sm klg-all" disabled title="${escapeHtml(sperrgrund(img))}">alle</button>`}
              <code class="klg-alias">${escapeHtml(img.alias)}</code>
              ${img.releasable ? '' : `<div class="klg-sperre">${escapeHtml(sperrgrund(img))}</div>`}
            </div>
          </td>
          ${zellen}
        </tr>`;
    }).join('');

    const offen = changedColumns(start, zustand).length;
    const hinweis = sperrHinweis(gesperrt.size, bereinigt.entfernt);
    // Speichern steht OBEN RECHTS, nicht unter der Tabelle: Die Matrix scrollt in sich, und
    // ein Knopf unter dem Scrollbereich waere bei zwoelf Bildern ausserhalb des Blickfelds,
    // genau wenn man ihn braucht.
    host.innerHTML = `
      <div class="klg-shell">
        <div class="klg-kopf">
          <div class="klg-hint">
            Klicke eine Zelle, um das Bild der Variante zuzuordnen. Die Zahl ist die
            Anzeige-Reihenfolge je Variante. Mit ▲▼ sortieren, Klick auf die Zahl entfernt die Zuordnung.
          </div>
          ${hinweis ? `<div class="klg-sperre-hinweis">${escapeHtml(hinweis)}</div>` : ''}
          <div class="klg-kopf-aktionen">
            <span class="v-text-muted klg-stand">${offen === 0 ? 'Keine Änderungen' : `${offen} Variante(n) geändert`}</span>
            <button type="button" class="v-button v-button--primary v-button--sm klg-save"${offen === 0 ? ' disabled' : ''}>Bilder speichern</button>
          </div>
        </div>
        <div class="klg-scroll">
          <table class="klg-table">
            <thead><tr><th class="klg-bild">Bild</th>${kopf}</tr></thead>
            <tbody>${koerper}</tbody>
          </table>
        </div>
      </div>`;

    // Scroll-Stand zurueckstellen. Synchron direkt nach dem innerHTML-Schreiben, also noch
    // vor dem naechsten Frame -- so wird das Springen nicht sichtbar.
    const neuerBereich = host.querySelector<HTMLElement>('.klg-scroll');
    if (neuerBereich) {
      neuerBereich.scrollTop = merkeOben;
      neuerBereich.scrollLeft = merkeLinks;
    }

    verdrahten();
    void vorschauenNachladen();
  };

  /**
   * Laedt die Vorschauen nicht freigegebener Bilder ueber `vario-dms-image-proxy`.
   *
   * MIT BEGRENZTER NEBENLAEUFIGKEIT, seit dem 2026-08-16. Vorher lief die Schleife seriell,
   * mit derselben Begruendung wie im Bilder-Dialog: ein Schwall ueberschreitet Varios
   * Rate-Limit. Der Haendler hat gemessen, was das praktisch heisst -- "das erste Bild kommt
   * schnell, dann dauert es wieder", weil jedes Bild auf das vorige wartet.
   *
   * WARUM NICHT ALLE AUF EINMAL, obwohl genau das gefragt war: Eine Familie kann 40 Bilder
   * haben. Vierzig gleichzeitige Anfragen sind der "Burst", der den 429-Vorfall ausgeloest
   * hat, und Varios Limit kennen wir nicht. Sechs gleichzeitig ist fuer die uebliche Familie
   * praktisch "alle auf einmal" -- bei neun Bildern zwei Wellen statt neun -- und bleibt bei
   * einer grossen Familie beherrschbar. Der Proxy hat zusaetzlich einen 429-Backoff.
   */
  const PARALLEL = 6;

  const vorschauenNachladen = async (): Promise<void> => {
    const offen = [...host.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy')];
    let naechster = 0;

    const einLader = async (): Promise<void> => {
      for (;;) {
        const i = naechster++;
        if (i >= offen.length) return;
        const el = offen[i]!;
        const rid = el.dataset['rid'];
        const aid = el.dataset['aid'];
        if (!rid || !aid || !el.isConnected) continue;
        const cached = vorschauCache.get(rid);
        if (cached) { el.src = cached; el.classList.remove('klg-thumb--lade'); continue; }
        try {
          const r = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-dms-image-proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
            body: JSON.stringify({ articleId: aid, resourceId: rid }),
          });
          if (!r.ok) continue;
          const url = URL.createObjectURL(await r.blob());
          vorschauCache.set(rid, url);
          if (!el.isConnected) continue;
          el.src = url;
          el.classList.remove('klg-thumb--lade');
        } catch (e) {
          console.warn('[bild-grid] Vorschau fehlgeschlagen', rid, (e as Error).message);
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(PARALLEL, offen.length) }, einLader));
  };

  const verdrahten = (): void => {
    host.querySelectorAll<HTMLButtonElement>('[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const col = btn.dataset['col'] ?? '';
        const res = btn.dataset['res'] ?? '';
        const act = btn.dataset['act'];
        if (act === 'add' && !gesperrt.has(res)) zustand = assignImage(zustand, col, res);
        else if (act === 'remove') zustand = removeImage(zustand, col, res);
        else if (act === 'up') zustand = moveImage(zustand, col, res, 'hoch');
        else if (act === 'down') zustand = moveImage(zustand, col, res, 'runter');
        zeichnen();
      });
    });

    host.querySelectorAll<HTMLButtonElement>('.klg-all').forEach((btn) => {
      btn.addEventListener('click', () => {
        // Bewusst NUR die Kind-Spalten: Der Vater ist kein Kaufland-Produkt.
        zustand = assignToAll(zustand, kindSpalten, btn.dataset['res'] ?? '');
        zeichnen();
      });
    });

    host.querySelector<HTMLButtonElement>('.klg-save')?.addEventListener('click', () => {
      void speichern();
    });
  };

  const speichern = async (): Promise<void> => {
    const btn = host.querySelector<HTMLButtonElement>('.klg-save');
    if (btn) { btn.disabled = true; btn.textContent = 'Speichere …'; }

    // NUR geaenderte Spalten: Jeder Aufruf loest je Bild einen ensure_dms_share-Job aus.
    const zuSpeichern = changedColumns(start, zustand).filter((c) => kindSpalten.includes(c) || articles.some((a) => a.articleId === c && !a.isParent));
    let fehler = 0;
    for (const col of zuSpeichern) {
      const releases = orderedResources(zustand, col).map((res, i) => {
        const img = zeilen.get(res);
        return { varioResourceId: res, sortOrder: i, mimeType: img?.mimeType, alias: img?.alias };
      });
      const r = await callFn<{ ok: true }>(ctx, 'kaufland-release-images', { articleId: col, releases });
      if (!r.ok) { fehler++; console.warn('[bild-grid] Spalte fehlgeschlagen', col, r.error); }
    }

    if (fehler > 0) {
      showToast(`${fehler} von ${zuSpeichern.length} Varianten konnten nicht gespeichert werden`, 'error');
      if (btn) { btn.disabled = false; btn.textContent = 'Bilder speichern'; }
      return;
    }
    showToast('Bilder gespeichert', 'success');
    // Neu laden statt den Zustand zu erraten: Die Freigabe erzeugt Shares, und erst danach
    // gibt es fuer neue Bilder eine public_url fuer die Vorschau.
    await renderVariantImageGrid(host, ctx, articles);
  };

  zeichnen();
}
